import { randf } from "@typegpu/noise";
import * as sdf from "@typegpu/sdf";
import type {
  TgpuBindGroup,
  TgpuFixedSampler,
  TgpuRenderPipeline,
  TgpuRoot,
  TgpuTextureView,
  TgpuUniform,
} from "typegpu";
import tgpu from "typegpu";
import { fullScreenTriangle } from "typegpu/common";
import * as d from "typegpu/data";
import * as std from "typegpu/std";
import { Camera, CameraController } from "../common/camera.ts";
import {
  AMBIENT_COLOR,
  AMBIENT_INTENSITY,
  AO_BIAS,
  AO_INTENSITY,
  AO_RADIUS,
  AO_STEPS,
  GROUND_ALBEDO,
  JELLY_IOR,
  JELLY_SCATTER_STRENGTH,
  LINE_HALF_THICK,
  LINE_RADIUS,
  MAX_DIST,
  MAX_STEPS,
  SPECULAR_INTENSITY,
  SPECULAR_POWER,
  SURF_DIST,
} from "./constants.ts";
import {
  DirectionalLight,
  HitInfo,
  LineInfo,
  MaterialContext,
  ObjectType,
  Ray,
  rayMarchLayout,
  SdfBbox,
  sampleLayout,
} from "./data-types.ts";
import { Slider } from "./slider.ts";
import { TAAResolver } from "./taa.ts";
import {
  beerLambert,
  createBackgroundTexture,
  createTextures,
  fresnelSchlick,
  intersectBox,
} from "./utils.ts";

export interface JellySliderOptions {
  root: TgpuRoot;
  targetFormat: GPUTextureFormat;
  jellyColor?: d.v3f | ((ctx: MaterialContext) => d.v3f) | undefined;
  glowTint?: d.v3f | ((x: number) => d.v3f) | undefined;
  material?: ((ctx: MaterialContext) => d.v3f) | undefined;
  /**
   * @default true
   */
  refractedHighlight?: boolean | undefined;
}

const lightAccess = tgpu.const(DirectionalLight, {
  direction: std.normalize(d.vec3f(0.18, -0.3, 0.64)),
  color: d.vec3f(1, 1, 1),
});

const cameraAccess = tgpu["~unstable"].accessor(Camera);
const endCapAccess = tgpu["~unstable"].accessor(d.vec4f);
const taaPrimerAccess = tgpu["~unstable"].accessor(d.vec2f);

const sliderBBoxSlot =
  tgpu.slot<[top: number, right: number, bottom: number, left: number]>();

const constantAlbedoSlot = tgpu.slot(d.vec3f(1.0, 0.45, 0.075));

const getAlbedoSlot = tgpu.slot((ctx: MaterialContext): d.v3f => {
  "use gpu";
  return constantAlbedoSlot.$;
});

const constantGlowTintSlot = tgpu.slot(d.vec3f(1.0, 0.45, 0.075));

const getGlowTintSlot = tgpu.slot((x: number): d.v3f => {
  "use gpu";
  return constantGlowTintSlot.$;
});

const staticSlot = tgpu.slot<{
  refractedHighlight: boolean;
  bezierTexture: TgpuTextureView<d.WgslTexture2d<d.F32>>;
  filteringSampler: TgpuFixedSampler;
}>();

type BindGroups = {
  rayMarch: TgpuBindGroup<(typeof rayMarchLayout)["entries"]>;
  render: TgpuBindGroup<(typeof sampleLayout)["entries"]>[];
};

export const defaultJellyMaterial = (ctx: MaterialContext): d.v3f => {
  "use gpu";

  if (ctx.k <= 0) {
    // No refraction
    return ctx.reflection.mul(ctx.fresnel);
  }

  const jellyColor = getAlbedoSlot.$(ctx);
  const scatterTint = jellyColor.xyz.mul(1.5);
  const density = d.f32(20.0);
  const absorb = d.vec3f(1.0).sub(jellyColor.xyz).mul(density);

  const T = beerLambert(absorb.mul(ctx.uv.x ** 2), 0.08);

  const lightDir = std.neg(lightAccess.$.direction);

  const forward = std.max(0.0, std.dot(lightDir, ctx.refrDir));
  const scatter = scatterTint.mul(
    JELLY_SCATTER_STRENGTH * forward * ctx.uv.x ** 3,
  );
  const refractedColor = ctx.env.mul(T).add(scatter);

  return std.add(
    ctx.reflection.mul(ctx.fresnel),
    refractedColor.mul(1 - ctx.fresnel),
  );
};

const materialSlot = tgpu.slot(defaultJellyMaterial);

export class JellySlider {
  readonly root: TgpuRoot;

  #slider: Slider;
  #camera: CameraController;
  #taaResolver: TAAResolver;
  #randomUniform: TgpuUniform<d.Vec2f>;
  #rayMarchPipeline: TgpuRenderPipeline;
  #renderPipeline: TgpuRenderPipeline;

  #width: number | undefined;
  #height: number | undefined;
  #qualityScale: number;
  #frameCount: number;
  #textures: ReturnType<typeof createTextures>;
  #backgroundTexture: ReturnType<typeof createBackgroundTexture>;
  #bindGroups: BindGroups;
  #value: number;

  constructor(options: JellySliderOptions) {
    this.root = options.root;
    this.#randomUniform = this.root.createUniform(d.vec2f);

    this.#qualityScale = 1;
    this.#frameCount = 0;
    this.#textures = createTextures(this.root, 1, 1);
    this.#backgroundTexture = createBackgroundTexture(this.root, 1, 1);
    this.#value = 0;

    const hasTimestampQuery = this.root.enabledFeatures.has("timestamp-query");

    const NUM_POINTS = 17;

    this.#taaResolver = new TAAResolver(this.root, 1, 1);

    this.#slider = new Slider(
      this.root,
      d.vec2f(-1, 0),
      d.vec2f(0.9, 0),
      NUM_POINTS,
      -0.03,
    );

    const bezierTexture = this.#slider.bezierTexture.createView();
    const bezierBbox = this.#slider.bbox;

    const filteringSampler = this.root["~unstable"].createSampler({
      magFilter: "linear",
      minFilter: "linear",
    });

    this.#camera = new CameraController(
      this.root,
      d.vec3f(0.024, 2.7, 1.9),
      d.vec3f(0, 0, 0),
      d.vec3f(0, 1, 0),
      Math.PI / 4,
      1,
      1,
    );

    this.#bindGroups = this.#createBindGroups();

    this.#rayMarchPipeline = this.root["~unstable"]
      .with(taaPrimerAccess, this.#randomUniform)
      .with(cameraAccess, this.#camera.cameraUniform)
      .with(endCapAccess, this.#slider.endCapUniform)
      .with(staticSlot, {
        bezierTexture,
        filteringSampler,
        refractedHighlight: options.refractedHighlight ?? true,
      })
      .with(sliderBBoxSlot, bezierBbox)
      .pipe((cfg) => {
        if (!options.jellyColor) {
          return cfg;
        }

        if ((options.jellyColor as d.v3f).kind === "vec3f") {
          return cfg.with(constantAlbedoSlot, options.jellyColor as d.v3f);
        }

        return cfg.with(getAlbedoSlot, options.jellyColor);
      })
      .pipe((cfg) => {
        if (!options.glowTint) {
          return cfg;
        }

        if ((options.glowTint as d.v3f).kind === "vec3f") {
          return cfg.with(constantGlowTintSlot, options.glowTint as d.v3f);
        }

        return cfg.with(getGlowTintSlot, options.glowTint);
      })
      .pipe((cfg) =>
        options.material ? cfg.with(materialSlot, options.material) : cfg,
      )
      .withVertex(fullScreenTriangle, {})
      .withFragment(raymarchFn, { format: "rgba8unorm" })
      .createPipeline();

    this.#renderPipeline = this.root["~unstable"]
      .with(staticSlot, {
        bezierTexture,
        filteringSampler,
        refractedHighlight: options.refractedHighlight ?? true,
      })
      .withVertex(fullScreenTriangle, {})
      .withFragment(fragmentMain, { format: options.targetFormat })
      .createPipeline();
  }

  #createBindGroups() {
    return {
      rayMarch: this.root.createBindGroup(rayMarchLayout, {
        backgroundTexture: this.#backgroundTexture.sampled,
      }),
      render: [0, 1].map((frame) =>
        this.root.createBindGroup(sampleLayout, {
          currentTexture: this.#taaResolver.getResolvedTexture(frame),
        }),
      ),
    };
  }

  get value(): number {
    return this.#value;
  }

  set value(v: number) {
    this.#value = v;
    this.#slider.setDragX(v);
  }

  handleResize(width: number, height: number) {
    if (this.#width === width && this.#height === height) {
      return;
    }
    this.#width = width;
    this.#height = height;

    this.#camera.updateProjection(Math.PI / 4, width, height);
    this.#textures = createTextures(this.root, width, height);
    this.#backgroundTexture = createBackgroundTexture(this.root, width, height);
    this.#taaResolver.resize(width, height);
    this.#frameCount = 0;
    this.#bindGroups = this.#createBindGroups();
  }

  update(deltaSeconds: number): void {
    this.#slider.update(deltaSeconds);
  }

  render(targetView: GPUTextureView, width: number, height: number): void {
    this.handleResize(width * this.#qualityScale, height * this.#qualityScale);

    this.#frameCount++;
    this.#camera.jitter();

    this.#randomUniform.write(
      d.vec2f((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2),
    );

    const currentFrame = this.#frameCount % 2;

    this.#rayMarchPipeline
      .withColorAttachment({
        view: this.root.unwrap(this.#textures[currentFrame].sampled),
        loadOp: "clear",
        storeOp: "store",
      })
      .draw(3);

    this.#taaResolver.resolve(
      this.#textures[currentFrame].sampled,
      this.#frameCount,
      currentFrame,
    );

    this.#renderPipeline
      .withColorAttachment({
        view: targetView,
        loadOp: "clear",
        storeOp: "store",
      })
      .with(this.#bindGroups.render[currentFrame])
      .draw(3);
  }
}

const getRay = (ndc: d.v2f) => {
  "use gpu";
  const clipPos = d.vec4f(ndc.x, ndc.y, -1.0, 1.0);

  const invView = cameraAccess.$.viewInv;
  const invProj = cameraAccess.$.projInv;

  const viewPos = invProj.mul(clipPos);
  const viewPosNormalized = d.vec4f(viewPos.xyz.div(viewPos.w), 1.0);

  const worldPos = invView.mul(viewPosNormalized);

  const rayOrigin = invView.columns[3].xyz;
  const rayDir = std.normalize(worldPos.xyz.sub(rayOrigin));

  return Ray({
    origin: rayOrigin,
    direction: rayDir,
  });
};

const getSliderBbox = () => {
  "use gpu";
  return SdfBbox({
    left: d.f32(sliderBBoxSlot.$[3]),
    right: d.f32(sliderBBoxSlot.$[1]),
    bottom: d.f32(sliderBBoxSlot.$[2]),
    top: d.f32(sliderBBoxSlot.$[0]),
  });
};

const sdInflatedPolyline2D = (p: d.v2f) => {
  "use gpu";
  const bbox = getSliderBbox();

  const uv = d.vec2f(
    (p.x - bbox.left) / (bbox.right - bbox.left),
    (bbox.top - p.y) / (bbox.top - bbox.bottom),
  );
  const clampedUV = std.saturate(uv);

  const sampledColor = std.textureSampleLevel(
    staticSlot.$.bezierTexture.$,
    staticSlot.$.filteringSampler.$,
    clampedUV,
    0,
  );
  const segUnsigned = sampledColor.x;
  const progress = sampledColor.y;
  const normal = sampledColor.zw;

  return LineInfo({
    t: progress,
    distance: segUnsigned,
    normal: normal,
  });
};

const cap3D = (position: d.v3f) => {
  "use gpu";
  const endCap = endCapAccess.$;
  const secondLastPoint = d.vec2f(endCap.x, endCap.y);
  const lastPoint = d.vec2f(endCap.z, endCap.w);

  const angle = std.atan2(
    lastPoint.y - secondLastPoint.y,
    lastPoint.x - secondLastPoint.x,
  );
  const rot = d.mat2x2f(
    std.cos(angle),
    -std.sin(angle),
    std.sin(angle),
    std.cos(angle),
  );

  let pieP = position.sub(d.vec3f(secondLastPoint, 0));
  pieP = d.vec3f(rot.mul(pieP.xy), pieP.z);
  const hmm = sdf.sdPie(pieP.zx, d.vec2f(1, 0), LINE_HALF_THICK);
  const extrudeEnd = sdf.opExtrudeY(pieP, hmm, 0.001) - LINE_RADIUS;
  return extrudeEnd;
};

const sliderSdf3D = (position: d.v3f) => {
  "use gpu";
  const poly2D = sdInflatedPolyline2D(position.xy);

  let finalDist = d.f32(0.0);
  if (poly2D.t > 0.94) {
    finalDist = cap3D(position);
  } else {
    const body =
      sdf.opExtrudeZ(position, poly2D.distance, LINE_HALF_THICK) - LINE_RADIUS;
    finalDist = body;
  }

  return LineInfo({
    t: poly2D.t,
    distance: finalDist,
    normal: poly2D.normal,
  });
};

const GroundParams = {
  groundThickness: 0.03,
  groundRoundness: 0.02,
};

const rectangleCutoutDist = (position: d.v2f) => {
  "use gpu";
  const groundRoundness = GroundParams.groundRoundness;

  return sdf.sdRoundedBox2d(
    position,
    d.vec2f(1 + groundRoundness, 0.2 + groundRoundness),
    0.2 + groundRoundness,
  );
};

const getMainSceneDist = (position: d.v3f) => {
  "use gpu";
  const groundThickness = GroundParams.groundThickness;
  const groundRoundness = GroundParams.groundRoundness;

  return sdf.opUnion(
    sdf.sdPlane(position, d.vec3f(0, 1, 0), 0.06),
    sdf.opExtrudeY(
      position,
      -rectangleCutoutDist(position.xz),
      groundThickness - groundRoundness,
    ) - groundRoundness,
  );
};

const sliderApproxDist = (position: d.v3f) => {
  "use gpu";
  const bbox = getSliderBbox();

  const p = position.xy;
  if (
    p.x < bbox.left ||
    p.x > bbox.right ||
    p.y < bbox.bottom ||
    p.y > bbox.top
  ) {
    return 1e9;
  }

  const poly2D = sdInflatedPolyline2D(p);
  const dist3D =
    sdf.opExtrudeZ(position, poly2D.distance, LINE_HALF_THICK) - LINE_RADIUS;

  return dist3D;
};

const getSceneDist = (position: d.v3f) => {
  "use gpu";
  const mainScene = getMainSceneDist(position);
  const poly3D = sliderSdf3D(position);

  const hitInfo = HitInfo();

  if (poly3D.distance < mainScene) {
    hitInfo.distance = poly3D.distance;
    hitInfo.objectType = ObjectType.SLIDER;
    hitInfo.t = poly3D.t;
  } else {
    hitInfo.distance = mainScene;
    hitInfo.objectType = ObjectType.BACKGROUND;
  }
  return hitInfo;
};

const getSceneDistForAO = (position: d.v3f) => {
  "use gpu";
  const mainScene = getMainSceneDist(position);
  const sliderApprox = sliderApproxDist(position);
  return std.min(mainScene, sliderApprox);
};

const sdfSlot = tgpu.slot<(pos: d.v3f) => number>();

const getNormalFromSdf = tgpu.fn(
  [d.vec3f, d.f32],
  d.vec3f,
)((position, epsilon) => {
  "use gpu";
  const k = d.vec3f(1, -1, 0);

  const offset1 = k.xyy.mul(epsilon);
  const offset2 = k.yyx.mul(epsilon);
  const offset3 = k.yxy.mul(epsilon);
  const offset4 = k.xxx.mul(epsilon);

  const sample1 = offset1.mul(sdfSlot.$(position.add(offset1)));
  const sample2 = offset2.mul(sdfSlot.$(position.add(offset2)));
  const sample3 = offset3.mul(sdfSlot.$(position.add(offset3)));
  const sample4 = offset4.mul(sdfSlot.$(position.add(offset4)));

  const gradient = sample1.add(sample2).add(sample3).add(sample4);

  return std.normalize(gradient);
});

const getNormalCapSdf = getNormalFromSdf.with(sdfSlot, cap3D);
const getNormalMainSdf = getNormalFromSdf.with(sdfSlot, getMainSceneDist);

const getNormalCap = (pos: d.v3f) => {
  "use gpu";
  return getNormalCapSdf(pos, 0.01);
};

const getNormalMain = (position: d.v3f) => {
  "use gpu";
  if (std.abs(position.z) > 0.22 || std.abs(position.x) > 1.02) {
    return d.vec3f(0, 1, 0);
  }
  return getNormalMainSdf(position, 0.0001);
};

const getSliderNormal = (position: d.v3f, hitInfo: d.Infer<typeof HitInfo>) => {
  "use gpu";
  const poly2D = sdInflatedPolyline2D(position.xy);
  const gradient2D = poly2D.normal;

  const threshold = LINE_HALF_THICK * 0.85;
  const absZ = std.abs(position.z);
  const zDistance = std.max(
    0,
    ((absZ - threshold) * LINE_HALF_THICK) / (LINE_HALF_THICK - threshold),
  );
  const edgeDistance = LINE_RADIUS - poly2D.distance;

  const edgeContrib = 0.9;
  const zContrib = 1.0 - edgeContrib;

  const zDirection = std.sign(position.z);
  const zAxisVector = d.vec3f(0, 0, zDirection);

  const edgeBlendDistance =
    edgeContrib * LINE_RADIUS + zContrib * LINE_HALF_THICK;

  const blendFactor = std.smoothstep(
    edgeBlendDistance,
    0.0,
    zDistance * zContrib + edgeDistance * edgeContrib,
  );

  const normal2D = d.vec3f(gradient2D.xy, 0);
  const blendedNormal = std.mix(zAxisVector, normal2D, blendFactor * 0.5 + 0.5);

  let normal = std.normalize(blendedNormal);

  if (hitInfo.t > 0.94) {
    const ratio = (hitInfo.t - 0.94) / 0.02;
    const fullNormal = getNormalCap(position);
    normal = std.normalize(std.mix(normal, fullNormal, ratio));
  }

  return normal;
};

const getNormal = (position: d.v3f, hitInfo: d.Infer<typeof HitInfo>) => {
  "use gpu";
  if (hitInfo.objectType === ObjectType.SLIDER && hitInfo.t < 0.96) {
    return getSliderNormal(position, hitInfo);
  }

  return std.select(
    getNormalCap(position),
    getNormalMain(position),
    hitInfo.objectType === ObjectType.BACKGROUND,
  );
};

const sqLength = (a: d.v3f) => {
  "use gpu";
  return std.dot(a, a);
};

const getFakeShadow = (position: d.v3f, lightDir: d.v3f): d.v3f => {
  "use gpu";
  const endCapX = endCapAccess.$.x;

  if (position.y < -GroundParams.groundThickness) {
    // Applying darkening under the ground (the shadow cast by the upper ground layer)
    const fadeSharpness = d.f32(30);
    const inset = 0.02;
    const cutout = rectangleCutoutDist(position.xz) + inset;
    const edgeDarkening = std.saturate(1 - cutout * fadeSharpness);

    // Applying a slight gradient based on the light direction
    const lightGradient = std.saturate(-position.z * 4 * lightDir.z + 1);

    return d
      .vec3f(1)
      .mul(edgeDarkening)
      .mul(lightGradient * 0.5);
  } else {
    const finalUV = d.vec2f(
      (position.x - position.z * lightDir.x * std.sign(lightDir.z)) * 0.5 + 0.5,
      1 - (-position.z / lightDir.z) * 0.5 - 0.2,
    );
    const data = std.textureSampleLevel(
      staticSlot.$.bezierTexture.$,
      staticSlot.$.filteringSampler.$,
      finalUV,
      0,
    );

    // Normally it would be just data.y, but there transition is too sudden when the jelly is bunched up.
    // To mitigate this, we transition into a position-based transition.
    const jellySaturation = std.mix(
      d.f32(0),
      data.y,
      std.saturate(position.x * 1.5 + 1.1),
    );
    const jellyColor = getGlowTintSlot.$(jellySaturation);
    const shadowColor = std.mix(
      d.vec3f(0, 0, 0),
      jellyColor.xyz,
      jellySaturation,
    );

    const contrast = 20 * std.saturate(finalUV.y) * (0.8 + endCapX * 0.2);
    const shadowOffset = -0.3;
    const featherSharpness = d.f32(10);
    const uvEdgeFeather =
      std.saturate(finalUV.x * featherSharpness) *
      std.saturate((1 - finalUV.x) * featherSharpness) *
      std.saturate((1 - finalUV.y) * featherSharpness) *
      std.saturate(finalUV.y);
    const influence = std.saturate((1 - lightDir.y) * 2) * uvEdgeFeather;
    return std.mix(
      d.vec3f(1),
      std.mix(
        shadowColor,
        d.vec3f(1),
        std.saturate(data.x * contrast + shadowOffset),
      ),
      influence,
    );
  }
};

const calculateAO = (position: d.v3f, normal: d.v3f) => {
  "use gpu";
  let totalOcclusion = d.f32(0.0);
  let sampleWeight = d.f32(1.0);
  const stepDistance = AO_RADIUS / AO_STEPS;

  for (let i = 1; i <= AO_STEPS; i++) {
    const sampleHeight = stepDistance * d.f32(i);
    const samplePosition = position.add(normal.mul(sampleHeight));
    const distanceToSurface = getSceneDistForAO(samplePosition) - AO_BIAS;
    const occlusionContribution = std.max(
      0.0,
      sampleHeight - distanceToSurface,
    );
    totalOcclusion += occlusionContribution * sampleWeight;
    sampleWeight *= 0.5;
    if (totalOcclusion > AO_RADIUS / AO_INTENSITY) {
      break;
    }
  }

  const rawAO = 1.0 - (AO_INTENSITY * totalOcclusion) / AO_RADIUS;
  return std.saturate(rawAO);
};

const calculateLighting = (
  hitPosition: d.v3f,
  normal: d.v3f,
  rayOrigin: d.v3f,
) => {
  "use gpu";
  const lightDir = std.neg(lightAccess.$.direction);

  const fakeShadow = getFakeShadow(hitPosition, lightDir);
  const diffuse = std.max(std.dot(normal, lightDir), 0.0);

  const viewDir = std.normalize(rayOrigin.sub(hitPosition));
  const reflectDir = std.reflect(std.neg(lightDir), normal);
  const specularFactor =
    std.max(std.dot(viewDir, reflectDir), 0) ** SPECULAR_POWER;
  const specular = lightAccess.$.color.mul(specularFactor * SPECULAR_INTENSITY);

  const baseColor = d.vec3f(0.9);

  const directionalLight = baseColor
    .mul(lightAccess.$.color)
    .mul(diffuse)
    .mul(fakeShadow);
  const ambientLight = baseColor.mul(AMBIENT_COLOR).mul(AMBIENT_INTENSITY);

  const finalSpecular = specular.mul(fakeShadow);

  return std.saturate(directionalLight.add(ambientLight).add(finalSpecular));
};

const applyAO = (litColor: d.v3f, hitPosition: d.v3f, normal: d.v3f) => {
  "use gpu";
  const ao = calculateAO(hitPosition, normal);
  const finalColor = litColor.mul(ao);
  return d.vec4f(finalColor, 1.0);
};

const rayMarchNoJelly = (rayOrigin: d.v3f, rayDirection: d.v3f) => {
  "use gpu";
  let distanceFromOrigin = d.f32();
  let hit = d.f32();

  for (let i = 0; i < 6; i++) {
    const p = rayOrigin.add(rayDirection.mul(distanceFromOrigin));
    hit = getMainSceneDist(p);
    distanceFromOrigin += hit;
    if (distanceFromOrigin > MAX_DIST || hit < SURF_DIST * 10) {
      break;
    }
  }

  if (distanceFromOrigin < MAX_DIST) {
    return renderBackground(
      rayOrigin,
      rayDirection,
      distanceFromOrigin,
      d.f32(0),
    ).xyz;
  }
  return d.vec3f();
};

const renderPercentageOnGround = (
  hitPosition: d.v3f,
  center: d.v3f,
  percentage: number,
) => {
  "use gpu";

  const textWidth = 0.38;
  const textHeight = 0.33;

  if (
    std.abs(hitPosition.x - center.x) > textWidth * 0.5 ||
    std.abs(hitPosition.z - center.z) > textHeight * 0.5
  ) {
    return d.vec4f();
  }

  const localX = hitPosition.x - center.x;
  const localZ = hitPosition.z - center.z;

  const uvX = (localX + textWidth * 0.5) / textWidth;
  const uvZ = (localZ + textHeight * 0.5) / textHeight;

  if (uvX < 0.0 || uvX > 1.0 || uvZ < 0.0 || uvZ > 1.0) {
    return d.vec4f(0);
  }

  return d.vec4f(0);
  // return std.textureSampleLevel(
  //   digitsTextureView.$,
  //   filteringSampler.$,
  //   d.vec2f(uvX, uvZ),
  //   percentage,
  //   0,
  // );
};

const renderBackground = (
  rayOrigin: d.v3f,
  rayDirection: d.v3f,
  backgroundHitDist: number,
  offset: number,
) => {
  "use gpu";
  const hitPosition = rayOrigin.add(rayDirection.mul(backgroundHitDist));

  const percentageSample = renderPercentageOnGround(
    hitPosition,
    d.vec3f(0.72, 0, 0),
    d.u32((endCapAccess.$.x + 0.43) * 84),
  );

  let highlights = d.f32();

  const highlightWidth = d.f32(1);
  const highlightHeight = 0.2;
  let offsetX = d.f32();
  let offsetZ = d.f32(0.05);

  const lightDir = lightAccess.$.direction;
  const causticScale = 0.2;
  offsetX -= lightDir.x * causticScale;
  offsetZ += lightDir.z * causticScale;

  const endCapX = endCapAccess.$.x;
  const sliderStretch = (endCapX + 1) * 0.5;

  if (
    staticSlot.$.refractedHighlight &&
    std.abs(hitPosition.x + offsetX) < highlightWidth &&
    std.abs(hitPosition.z + offsetZ) < highlightHeight
  ) {
    const uvX_orig =
      ((hitPosition.x + offsetX + highlightWidth * 2) / highlightWidth) * 0.5;
    const uvZ_orig =
      ((hitPosition.z + offsetZ + highlightHeight * 2) / highlightHeight) * 0.5;

    const centeredUV = d.vec2f(uvX_orig - 0.5, uvZ_orig - 0.5);
    const finalUV = d.vec2f(
      centeredUV.x,
      1 - (std.abs(centeredUV.y - 0.5) * 2) ** 2 * 0.3,
    );

    const data = std.textureSampleLevel(
      staticSlot.$.bezierTexture.$,
      staticSlot.$.filteringSampler.$,
      finalUV,
      0,
    );
    const density = std.max(0, (data.x - 0.25) * 8);

    const fadeX = std.smoothstep(0, -0.2, hitPosition.x - endCapX);
    const fadeZ = 1 - (std.abs(centeredUV.y - 0.5) * 2) ** 3;
    const fadeStretch = std.saturate(1 - sliderStretch);
    const edgeFade = std.saturate(fadeX) * std.saturate(fadeZ) * fadeStretch;

    highlights = (density ** 3 * edgeFade * 3 * (1 + lightDir.z)) / 1.5;
  }

  const originYBound = std.saturate(rayOrigin.y + 0.01);
  const posOffset = hitPosition.add(
    d
      .vec3f(0, 1, 0)
      .mul(
        offset *
          (originYBound / (1.0 + originYBound)) *
          (1 + randf.sample() / 2),
      ),
  );
  const newNormal = getNormalMain(posOffset);

  // Calculate fake bounce lighting
  const jellyColor = getGlowTintSlot.$(hitPosition.x * 0.5 + 0.5);
  const sqDist = sqLength(hitPosition.sub(d.vec3f(endCapX, 0, 0)));
  const bounceLight = jellyColor.xyz.mul((1 / (sqDist * 15 + 1)) * 0.4);
  const sideBounceLight = jellyColor.xyz
    .mul((1 / (sqDist * 40 + 1)) * 0.3)
    .mul(std.abs(newNormal.z));

  const litColor = calculateLighting(posOffset, newNormal, rayOrigin);
  const backgroundColor = applyAO(
    GROUND_ALBEDO.mul(litColor),
    posOffset,
    newNormal,
  )
    .add(d.vec4f(bounceLight, 0))
    .add(d.vec4f(sideBounceLight, 0));

  const textColor = std.saturate(backgroundColor.xyz.mul(d.vec3f(0.5)));

  return d.vec4f(
    std
      .mix(backgroundColor.xyz, textColor, percentageSample.x)
      .mul(1.0 + highlights),
    1.0,
  );
};

const rayMarch = (rayOrigin: d.v3f, rayDirection: d.v3f, uv: d.v2f) => {
  "use gpu";
  let totalSteps = d.u32();

  let backgroundDist = d.f32();
  for (let i = 0; i < MAX_STEPS; i++) {
    const p = rayOrigin.add(rayDirection.mul(backgroundDist));
    const hit = getMainSceneDist(p);
    backgroundDist += hit;
    if (hit < SURF_DIST) {
      break;
    }
  }
  const background = renderBackground(
    rayOrigin,
    rayDirection,
    backgroundDist,
    d.f32(),
  );

  const bbox = getSliderBbox();
  const zDepth = d.f32(0.25);

  const sliderMin = d.vec3f(bbox.left, bbox.bottom, -zDepth);
  const sliderMax = d.vec3f(bbox.right, bbox.top, zDepth);

  const intersection = intersectBox(
    rayOrigin,
    rayDirection,
    sliderMin,
    sliderMax,
  );

  if (!intersection.hit) {
    return background;
  }

  let distanceFromOrigin = std.max(d.f32(0.0), intersection.tMin);

  for (let i = 0; i < MAX_STEPS; i++) {
    if (totalSteps >= MAX_STEPS) {
      break;
    }

    const currentPosition = rayOrigin.add(rayDirection.mul(distanceFromOrigin));

    const hitInfo = getSceneDist(currentPosition);
    distanceFromOrigin += hitInfo.distance;
    totalSteps++;

    if (hitInfo.distance < SURF_DIST) {
      const hitPosition = rayOrigin.add(rayDirection.mul(distanceFromOrigin));

      if (!(hitInfo.objectType === ObjectType.SLIDER)) {
        break;
      }

      const N = getNormal(hitPosition, hitInfo);
      const I = rayDirection;
      const cosi = std.min(1.0, std.max(0.0, std.dot(std.neg(I), N)));
      const F = fresnelSchlick(cosi, d.f32(1.0), d.f32(JELLY_IOR));

      const reflection = std.saturate(d.vec3f(hitPosition.y + 0.2));

      const progress = hitInfo.t;
      const eta = 1.0 / JELLY_IOR;
      const k = 1.0 - eta * eta * (1.0 - cosi * cosi);

      const materialCtx = MaterialContext({
        uv: d.vec2f(progress, hitPosition.z * 2.5 + 0.5),
        normal: N,
        fresnel: F,
        env: d.vec3f(),
        k,
        reflection,
        refrDir: d.vec3f(),
        lightDir: lightAccess.$.direction,
        viewDir: rayDirection,
      });

      if (k > 0.0) {
        const refrDir = std.normalize(
          std.add(I.mul(eta), N.mul(eta * cosi - std.sqrt(k))),
        );
        const p = hitPosition.add(refrDir.mul(SURF_DIST * 2.0));
        const exitPos = p.add(refrDir.mul(SURF_DIST * 2.0));

        materialCtx.env = rayMarchNoJelly(exitPos, refrDir);
        materialCtx.refrDir = refrDir;
      }

      const jelly = materialSlot.$(materialCtx);
      return d.vec4f(jelly, 1.0);
    }

    if (distanceFromOrigin > backgroundDist) {
      break;
    }
  }

  return background;
};

const raymarchFn = tgpu["~unstable"].fragmentFn({
  in: {
    uv: d.vec2f,
  },
  out: d.vec4f,
})(({ uv }) => {
  randf.seed2(taaPrimerAccess.$.mul(uv));

  const ndc = d.vec2f(uv.x * 2 - 1, -(uv.y * 2 - 1));
  const ray = getRay(ndc);

  const color = rayMarch(ray.origin, ray.direction, uv);
  return d.vec4f(std.tanh(color.xyz.mul(1.3)), 1);
});

const fragmentMain = tgpu["~unstable"].fragmentFn({
  in: { uv: d.vec2f },
  out: d.vec4f,
})((input) => {
  return std.textureSample(
    sampleLayout.$.currentTexture,
    staticSlot.$.filteringSampler.$,
    input.uv,
  );
});
